/**
 * Chunk Error Recovery (power#1298)
 *
 * Handles dynamic import failures that occur when a user's tab is open across
 * a deploy. When `aws s3 sync --delete` removes old chunks, lazy-loaded routes
 * fail until the user reloads.
 *
 * This module detects chunk load failures and surfaces the new-version banner
 * (via the app version store) so the user can reload with proper unsaved-work
 * checks. It never auto-reloads to avoid bypassing those checks.
 */

import { logger } from "@/services/logger";
import {
  fetchDeployedVersion,
  RUNNING_VERSION,
  hasAlreadyReloadedFor,
  isWithinReloadCooldown,
} from "@/utils/appVersion";
import { useAppVersionStore } from "@/stores/useAppVersionStore";

/**
 * Called when a chunk/dynamic-import fails.
 *
 * 1. Fetch /version.json to check for new deploy
 * 2. If new version exists AND safe to reload -> set pendingVersion (show banner)
 * 3. If no new version -> log but don't show banner (network error)
 *
 * Does NOT auto-reload: that would bypass unsaved-work checks.
 * The banner's reload button handles that properly.
 */
export async function handleChunkLoadError(error: Error): Promise<void> {
  logger.warn("Chunk load failed, checking for new version", {
    error: error.message,
  });

  const deployed = await fetchDeployedVersion();

  if (!deployed) {
    // Can't reach version.json - network issue
    logger.warn(
      "Cannot fetch version.json after chunk failure - network issue",
    );
    return;
  }

  if (deployed.version === RUNNING_VERSION) {
    // Same version - this is a network error, not a stale chunk
    // Don't show banner since there's no new version to update to
    logger.info("Chunk failed but version unchanged - likely network error");
    return;
  }

  // New version detected
  if (hasAlreadyReloadedFor(deployed.version)) {
    logger.warn(
      "Already reloaded for this version - bundle/version.json disagree",
    );
    return;
  }

  if (isWithinReloadCooldown()) {
    logger.info("Within reload cooldown, deferring banner");
    return;
  }

  logger.info("New version detected after chunk failure", {
    running: RUNNING_VERSION,
    deployed: deployed.version,
  });

  // Surface the banner - let user trigger reload (with unsaved work checks)
  useAppVersionStore.getState().setPending(deployed.version, deployed.builtAt);
}

/**
 * Check if an error is a chunk load failure.
 *
 * Chunk failures can manifest as:
 * 1. Network error (failed to fetch)
 * 2. MIME type error (CloudFront returned HTML for missing chunk)
 * 3. Syntax error (HTML parsed as JS)
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;

  const message = error.message.toLowerCase();

  // Dynamic import failures
  if (message.includes("failed to fetch dynamically imported module"))
    return true;
  if (message.includes("loading chunk")) return true;
  if (message.includes("loading css chunk")) return true;

  // MIME type errors (CloudFront HTML fallback)
  if (message.includes("mime type")) return true;
  if (message.includes("application/javascript")) return true;

  // Syntax errors from HTML being parsed as JS
  if (message.includes("unexpected token '<'")) return true;
  if (message.includes("unexpected token 'doctype'")) return true;

  return false;
}
